import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { ArrowLeft, SealCheck, TrendUp, ClockCounterClockwise, Buildings, MapPin, Globe, ArrowSquareOut, FilmStrip } from '@phosphor-icons/react';
import { motion } from 'framer-motion';
import { formatInrFriendly } from '../lib/inr';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const StatCard = ({ label, value, sub, tone = 'gold', testid }) => {
  const colors = {
    gold: 'text-gold border-gold/30 bg-gold/5',
    emerald: 'text-emerald-400 border-emerald-500/30 bg-emerald-500/5',
    cyan: 'text-cyan-400 border-cyan-500/30 bg-cyan-500/5',
    rose: 'text-rose-400 border-rose-500/30 bg-rose-500/5',
  };
  return (
    <div className={`border rounded-lg p-5 ${colors[tone]}`} data-testid={testid}>
      <div className="font-body text-[10px] uppercase tracking-wide text-slate-400 mb-1">{label}</div>
      <div className={`font-mono text-2xl font-bold tabular-nums ${colors[tone].split(' ')[0]}`}>{value}</div>
      {sub && <div className="font-body text-xs text-slate-400 mt-1">{sub}</div>}
    </div>
  );
};

const ProducerProfilePublic = ({ user }) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    (async () => {
      setLoading(true);
      try {
        const res = await axios.get(`${BACKEND_URL}/api/producers/${id}/profile`);
        if (active) setData(res.data);
      } catch (err) {
        toast.error('Producer not found');
        navigate('/marketplace');
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, [id, navigate]);

  if (loading || !data) {
    return <div className="min-h-screen bg-background flex items-center justify-center text-slate-400 font-body">Loading…</div>;
  }

  const { producer, profile, stats, verified_projects, active_movies } = data;
  const isOwner = user?.id === producer.id;

  return (
    <div className="min-h-screen bg-background">
      <div className="noise-overlay" />

      <nav data-web-chrome="nav" className="relative z-10 glass-card sticky top-0">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex justify-between items-center">
          <button onClick={() => navigate(-1)} className="flex items-center gap-2 text-slate-300 hover:text-white" data-testid="back-btn">
            <ArrowLeft size={18} /> <span className="font-body text-sm">Back</span>
          </button>
          {isOwner && (
            <button onClick={() => navigate('/producer/track-record')} className="btn-secondary text-sm py-2" data-testid="edit-track-record-btn">
              Edit track record
            </button>
          )}
        </div>
      </nav>

      <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Hero */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="bg-card border border-white/10 rounded-lg p-8 mb-8" data-testid="producer-hero">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
            <div>
              <div className="flex items-center gap-2 mb-2">
                <span className="text-xs uppercase tracking-wide text-slate-400 font-body">Producer</span>
                {stats.verified_projects > 0 && (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium border bg-emerald-500/10 text-emerald-300 border-emerald-500/30" data-testid="verified-track-record-badge">
                    <SealCheck size={12} weight="bold" /> Verified track record
                  </span>
                )}
              </div>
              <h1 className="font-heading text-5xl font-bold mb-2" data-testid="producer-name">{producer.name}</h1>
              {profile.company_name && <p className="font-body text-xl text-gold flex items-center gap-2"><Buildings size={18} /> {profile.company_name}</p>}
              <div className="mt-3 flex flex-wrap items-center gap-3 text-sm text-slate-400 font-body">
                {profile.location && <span className="inline-flex items-center gap-1"><MapPin size={14} /> {profile.location}</span>}
                {profile.years_active != null && <span>{profile.years_active}+ years in cinema</span>}
                {profile.website && (
                  <a href={profile.website} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 hover:text-gold">
                    <Globe size={14} /> {profile.website.replace(/^https?:\/\//, '')}
                  </a>
                )}
              </div>
              {profile.bio && <p className="mt-4 font-body text-slate-300 max-w-3xl">{profile.bio}</p>}
            </div>
          </div>
        </motion.div>

        {/* Stats grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-10" data-testid="producer-stats-grid">
          <StatCard testid="stat-verified" label="Verified projects" value={stats.verified_projects} sub="Admin-verified entries" />
          <StatCard testid="stat-avg-roi" label="Avg ROI" value={`${stats.avg_roi_pct > 0 ? '+' : ''}${stats.avg_roi_pct}%`} tone={stats.avg_roi_pct >= 0 ? 'emerald' : 'rose'} sub="Across verified projects" />
          <StatCard testid="stat-box-office" label="Total box office" value={`₹${formatInrFriendly(stats.total_box_office_inr)}`} tone="cyan" sub={`On ₹${formatInrFriendly(stats.total_budget_inr)} of budget`} />
          <StatCard testid="stat-on-time" label="On-time delivery" value={stats.on_time_delivery_rate == null ? '—' : `${stats.on_time_delivery_rate}%`} tone="gold" sub={stats.on_time_delivery_rate == null ? 'Not enough data' : 'Released on/before plan'} />
        </div>

        {/* Filmography */}
        <section className="mb-12">
          <h2 className="font-heading text-3xl font-bold mb-4">FILMOGRAPHY <span className="text-gold">·</span> Verified</h2>
          {verified_projects.length === 0 ? (
            <div className="bg-card border border-white/10 rounded-lg p-10 text-center text-slate-400 font-body">
              {isOwner ? 'No verified projects yet. Add projects from the track record page — they will appear here once approved.' : 'No verified projects yet.'}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="filmography-grid">
              {verified_projects.map(p => (
                <div key={p.id} className="bg-card border border-white/10 rounded-lg p-5" data-testid={`filmography-${p.id}`}>
                  <div className="flex items-start gap-4">
                    {p.poster_url && (
                      <img src={p.poster_url} alt={p.title} className="w-16 h-24 object-cover rounded ring-1 ring-white/10" onError={(e) => { e.currentTarget.style.display = 'none'; }} />
                    )}
                    <div className="flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <h3 className="font-heading text-lg font-bold">{p.title}</h3>
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-medium border bg-emerald-500/10 text-emerald-300 border-emerald-500/30">
                          <SealCheck size={10} weight="bold" /> Verified
                        </span>
                      </div>
                      <p className="text-xs text-slate-400 font-body mt-0.5">{p.role} · {p.industry} · {p.distribution}{p.platform ? ` (${p.platform})` : ''} · {p.release_date}</p>

                      <div className="grid grid-cols-3 gap-3 mt-3">
                        <div>
                          <div className="text-[10px] uppercase text-slate-400">Budget</div>
                          <div className="font-mono text-sm font-bold tabular-nums">₹{formatInrFriendly(p.budget_inr)}</div>
                        </div>
                        <div>
                          <div className="text-[10px] uppercase text-slate-400">Box office</div>
                          <div className="font-mono text-sm font-bold tabular-nums">₹{formatInrFriendly(p.box_office_inr)}</div>
                        </div>
                        <div>
                          <div className="text-[10px] uppercase text-slate-400">ROI</div>
                          <div className={`font-mono text-sm font-bold tabular-nums ${p.roi_pct >= 0 ? 'text-emerald-400' : 'text-rose-400'}`}>
                            {p.roi_pct > 0 ? '+' : ''}{p.roi_pct}%
                          </div>
                        </div>
                      </div>

                      <div className="mt-3 flex items-center gap-3 text-xs">
                        {p.on_time === true && <span className="text-emerald-300 inline-flex items-center gap-1"><ClockCounterClockwise size={12} /> On-time</span>}
                        {p.on_time === false && <span className="text-amber-300 inline-flex items-center gap-1"><ClockCounterClockwise size={12} /> Delayed</span>}
                        {p.imdb_url && <a href={p.imdb_url} target="_blank" rel="noreferrer" className="text-slate-400 hover:text-gold inline-flex items-center gap-1">IMDb <ArrowSquareOut size={10} /></a>}
                        {p.trailer_url && <a href={p.trailer_url} target="_blank" rel="noreferrer" className="text-slate-400 hover:text-gold inline-flex items-center gap-1">Trailer <ArrowSquareOut size={10} /></a>}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>

        {/* Active projects on the platform */}
        {active_movies && active_movies.length > 0 && (
          <section>
            <h2 className="font-heading text-2xl font-bold mb-4 flex items-center gap-2"><FilmStrip size={24} weight="bold" className="text-gold" /> Active on Fanzingy</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="active-movies-grid">
              {active_movies.map(m => (
                <button key={m.id} onClick={() => navigate(`/movie/${m.id}`)} className="bg-card border border-white/10 rounded-lg p-4 text-left hover:border-gold/40 transition-colors" data-testid={`active-movie-${m.id}`}>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-[10px] uppercase tracking-wide text-gold font-bold">{m.status}</span>
                    <span className="text-[10px] uppercase tracking-wide text-slate-400">{m.industry}</span>
                  </div>
                  <h3 className="font-heading text-lg font-bold mb-1">{m.title}</h3>
                  <p className="font-body text-xs text-slate-400 line-clamp-2">{m.description}</p>
                </button>
              ))}
            </div>
          </section>
        )}
      </div>
    </div>
  );
};

export default ProducerProfilePublic;
